import React, {PropsWithChildren, useMemo} from 'react';
import {Text} from '../Text';
import {cn} from '@/utils/common_functions';
import {StatusBadgeVariant} from '@/constants/enums';
import {fonts, palette} from '@/theme';

type StatusBadgeProps = PropsWithChildren<{
  status: StatusBadgeVariant;
  className?: string;
  textClassName?: string;
  variant?: 'primary' | 'secondary';
}>;

export const StatusBadge: React.FC<StatusBadgeProps> = props => {
  const {children, status, className, textClassName, variant = 'primary'} = props;
  const variants: Record<
    StatusBadgeVariant,
    {
      backgroundColor: string;
      textColor: string;
      fontFamily?: string;
    }
  > = {
    [StatusBadgeVariant.YELLOW]: {
      backgroundColor: palette.new,
      textColor: palette.black,
    },
    [StatusBadgeVariant.ORANGE]: {
      backgroundColor: palette.in_progress,
      textColor: palette.black,
    },
    [StatusBadgeVariant.ORANGE_LIGHT]: {
      backgroundColor: palette.in_progress,
      textColor: palette.white,
    },
    [StatusBadgeVariant.RED]: {
      backgroundColor: palette.cancel,
      textColor: palette.white,
    },
    [StatusBadgeVariant.GREEN]: {
      backgroundColor: palette.completed,
      textColor: palette.white,
    },
    [StatusBadgeVariant.BLUE]: {
      backgroundColor: palette.ui_bg_light_blue,
      textColor: palette.ui_blue,
    },
    [StatusBadgeVariant.GREY]: {
      backgroundColor: palette.grey_dark,
      textColor: palette.white,
    },
    [StatusBadgeVariant.DARK_BLUE]: {
      backgroundColor: palette.ui_blue_light,
      textColor: palette.white,
    },
    [StatusBadgeVariant.DARK_GRAY]: {
      backgroundColor: palette.ui_placeholder,
      textColor: palette.white,
    },
    [StatusBadgeVariant.FADE_BLUE]: {
      backgroundColor: palette.ui_input_border_dark,
      textColor: palette.white,
    },
    [StatusBadgeVariant.DARK_ORANGE]: {
      backgroundColor: palette.dark_orange,
      textColor: palette.white,
    },
    [StatusBadgeVariant.FADE_ORANGE]: {
      backgroundColor: palette.fade_orange,
      textColor: palette.white,
    },
    [StatusBadgeVariant.LIGHT_GREEN]: {
      backgroundColor: palette.light_green,
      textColor: palette.white,
    },
    [StatusBadgeVariant.PEARL_BLUE]: {
      backgroundColor: palette.pearl_blue,
      textColor: palette.white,
    },
    [StatusBadgeVariant.LIGHT_BLUE]: {
      backgroundColor: palette.ui_bg_light_blue,
      textColor: palette.ui_blue_background,
      fontFamily: fonts.FigtreeSemiBold,
    },
  };


  const containerStyles =  useMemo(() => {
    if (variant === 'primary') {
      return {
        backgroundColor: variants[status]?.backgroundColor,
        color: variants[status]?.textColor,
      };
    } else {
      return {
        borderColor: variants[status]?.backgroundColor,
        borderWidth: '1px',
        borderStyle: 'solid',
        color: variants[status]?.backgroundColor,

        // background color is the same as the border color but with 6.25% opacity
        backgroundColor: `${variants[status]?.backgroundColor}10`,
      };
    } 
  }, [status, variant]);
  return (
    <div
      style={containerStyles}
      className={cn(`px-3 py-1.5 inline-flex rounded-full`, className)}>
      <Text variant="free" className={cn('text-inherit text-xs sm:text-sm md:text-sm font-bold', textClassName)}>
        {children}
      </Text>
    </div>
  );
};
